// Per-word direction assignment for a Mix practice session (design:
// docs/design/practice-direction-controls.md §4). Mix = both directions pressed: every word in the
// deck drills in ONE direction, and the deck as a whole splits about half and half.
//
// The rule is FLIP-AND-BALANCE, fully deterministic (no RNG — the same deck + the same history
// always produces the same split, which is what makes "Practice again" a predictable mirror):
//   1. every word tentatively FLIPS from the direction it was last drilled in;
//   2. never-drilled words stay unassigned;
//   3. the split is balanced to nFwd = ceil(n/2): unassigned words fill the deficit side first
//      (term id ascending), then any remaining surplus is moved word by word, OLDEST last-drill
//      first (a stale repeat harms least), tie-break term id ascending.
//
// Pure string/number -> array: no DOM, no jQuery, no ajax — main.js fetches the history
// (api/practice/last-directions) and stamps the result onto the deck's cards. Node-tested the same
// way answer_forms.js is (src/test/js/direction_mix_test.js).
var DirectionMix = (function () {
	"use strict";

	var FORWARD = "FORWARD";     // cue = the meaning, the word is typed (MEANING_TO_TERM)
	var REVERSE = "REVERSE";     // cue = the word, the meaning is typed (TERM_TO_MEANING)

	// Sort helper: oldest last-drill first, never-drilled last, term id ascending as the tie-break.
	function byAgeThenId(a, b) {
		var ta = a.lastTs == null ? Infinity : a.lastTs;
		var tb = b.lastTs == null ? Infinity : b.lastTs;
		if (ta !== tb) { return ta - tb; }
		return byId(a, b);
	}

	// Term ids are integers in practice; a null id (never persisted) sorts last but stays stable.
	function byId(a, b) {
		var ia = a.termId == null ? Infinity : a.termId;
		var ib = b.termId == null ? Infinity : b.termId;
		if (ia !== ib) { return ia - ib; }
		return a.index - b.index;
	}

	// One word's drill history, from either payload shape: a bare direction string
	// ({"12": "REVERSE"}) or the full record ({"12": {direction: "REVERSE", ts: 1690000000000}}).
	// The timestamp is what the oldest-first rebalance needs; without it a word is simply
	// treated as the freshest (moved last).
	function historyOf(last, termId) {
		if (!last || termId == null) { return null; }
		var v = last[termId];
		if (v == null) { return null; }
		if (typeof v === "string") { return { direction: v, ts: null }; }
		return { direction: v.direction || null, ts: v.ts != null ? +v.ts : null };
	}

	/**
	 * Assign one direction per deck item.
	 *
	 * @param items [{termId, reversible}] in deck order. `reversible` false (a legacy sense-less
	 *              card: nothing to type in reverse) PINS the item to FORWARD — it is never moved
	 *              by the balance, but it does count towards the forward half.
	 * @param last  the last-drilled map keyed by term id (see historyOf for the accepted shapes).
	 * @return an array of "FORWARD"/"REVERSE", parallel to `items`.
	 */
	function assign(items, last) {
		var n = (items || []).length;
		var out = new Array(n);
		if (!n) { return out; }

		var entries = [];
		for (var i = 0; i < n; i++) {
			var it = items[i] || {};
			var h = historyOf(last, it.termId);
			entries.push({
				index: i,
				termId: it.termId != null ? +it.termId : null,
				movable: it.reversible !== false,
				lastDir: h ? h.direction : null,
				lastTs: h ? h.ts : null,
				dir: null
			});
		}

		// 1-2. Tentative: flip from the last direction; never-drilled stay unassigned.
		var fwd = 0, rev = 0;
		var unassigned = [];
		for (var e = 0; e < entries.length; e++) {
			var en = entries[e];
			if (!en.movable) {
				en.dir = FORWARD;          // pinned — counts as forward, never moved
			} else if (en.lastDir === REVERSE) {
				en.dir = FORWARD;
			} else if (en.lastDir === FORWARD) {
				en.dir = REVERSE;
			}
			if (en.dir === FORWARD) { fwd++; } else if (en.dir === REVERSE) { rev++; } else { unassigned.push(en); }
		}

		// 3. Balance. nFwd = ceil(n/2) — the odd word goes forward (production is the harder,
		// more valuable half, so it gets the extra slot).
		var nFwd = Math.ceil(n / 2);
		var needFwd = nFwd - fwd;
		var needRev = (n - nFwd) - rev;

		unassigned.sort(byId);
		for (var u = 0; u < unassigned.length; u++) {
			if (needFwd > 0) { unassigned[u].dir = FORWARD; needFwd--; }
			else { unassigned[u].dir = REVERSE; needRev--; }
		}

		// Whatever deficit survives the unassigned fill is a surplus on the other side: move
		// movable words across, oldest last-drill first.
		if (needFwd > 0) { move(entries, REVERSE, FORWARD, needFwd); }
		else if (needRev > 0) { move(entries, FORWARD, REVERSE, needRev); }

		for (var k = 0; k < entries.length; k++) { out[entries[k].index] = entries[k].dir; }
		return out;
	}

	function move(entries, from, to, count) {
		var pool = [];
		for (var i = 0; i < entries.length; i++) {
			if (entries[i].movable && entries[i].dir === from) { pool.push(entries[i]); }
		}
		pool.sort(byAgeThenId);
		for (var j = 0; j < pool.length && count > 0; j++, count--) { pool[j].dir = to; }
	}

	return {
		FORWARD: FORWARD,
		REVERSE: REVERSE,
		assign: assign
	};
})();
